import { LoaderCircle, MapPin, Sparkles, Star } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { discoveryApi } from '../../api/endpoints'
import type {
  EntityKind,
  LearnerNeedResponse,
  MapLearnerResponse,
  MapProviderResponse,
} from '../../types/api'
import { providerKindLabel } from '../../utils/lookingFor'
import { offeringLines } from '../../utils/offeringLines'
import { VerifiedBadge } from '../VerifiedBadge'

/** How far from the participant's saved location a suggestion may be. */
const RADIUS_KM = 5
const MAX_SUGGESTIONS = 5

type Status = 'loading' | 'ready' | 'failed'

/** One row in the panel, whichever side of the marketplace it came from. */
interface Suggestion {
  id: string
  title: string
  subtitle: string
  lines: string[]
  distanceKm: number
  rating?: { average: number; count: number }
  verified?: boolean
}

const fromProvider = (p: MapProviderResponse): Suggestion => ({
  id: p.id,
  title: p.fullName,
  subtitle: providerKindLabel(p.entityKind),
  lines: offeringLines(p.offerings, p.categories[0], p.subjects),
  distanceKm: p.distanceKm,
  rating: p.averageRating != null ? { average: p.averageRating, count: p.ratingCount } : undefined,
  verified: p.verificationStatus === 'VERIFIED',
})

// A learner is anonymous on the map until both sides accept - the API sends no name - so what
// identifies one here is what they are looking for.
const fromLearner = (l: MapLearnerResponse): Suggestion => ({
  id: l.id,
  title: l.fullName ?? 'A learner near you',
  subtitle: l.category ?? 'Looking for a provider',
  lines: offeringLines(l.offerings, l.category, l.subjects),
  distanceKm: l.distanceKm,
  rating: l.averageRating != null ? { average: l.averageRating, count: l.ratingCount } : undefined,
})

/**
 * The five nearest people within 5 km who fit this account, in the right-hand panel of the profile.
 *
 * <p>Which people is decided by the role, and changes with it: someone looking for something
 * (a student, a guardian) is shown providers who offer it - a tutor for lessons, a centre for exam
 * preparation, a school for an admission, a company for an internship - while someone offering
 * something (a tutor, a centre, a school, a college, a company) is shown the learners near them
 * who are looking for it. Both come from the same searches that drive the map, so the panel and
 * the map can never disagree about who is nearby.
 *
 * <p>Connect does not send anything from here. It opens the map with that person already selected,
 * where the connection is made with the usual consent and safety checks.
 */
export function NearbyRecommendations({
  audience,
  roleKey,
  needs,
  needsState = 'ready',
}: {
  /** DEMAND looks for providers, SUPPLY looks for learners. */
  audience: 'DEMAND' | 'SUPPLY'
  /** Changes when the active role does, so the search is redone for the new role. */
  roleKey: `${string}:${EntityKind}`
  needs: LearnerNeedResponse[]
  /**
   * Whether `needs` is an answer yet. Empty and not-yet-loaded look the same in `needs`, and telling
   * someone "add what you are looking for" before their needs have arrived (or after loading them
   * failed) is wrong on both counts.
   */
  needsState?: 'loading' | 'ready' | 'failed'
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [hasLocation, setHasLocation] = useState(true)
  const [status, setStatus] = useState<Status>('loading')
  const isDemand = audience === 'DEMAND'

  // Stands for the needs themselves: a new or removed need is a new search.
  const needsKey = needs.map((n) => `${n.id}:${n.category}`).join(',')

  useEffect(() => {
    if (isDemand && needsState !== 'ready') {
      setStatus(needsState === 'failed' ? 'failed' : 'loading')
      return
    }
    // A demand-side account with nothing it is looking for has nothing to match on.
    if (isDemand && needs.length === 0) {
      setSuggestions([])
      setStatus('ready')
      return
    }
    const controller = new AbortController()
    setStatus('loading')
    // Category only, on purpose: the panel is a nudge, so it is liberal - everyone nearby in a
    // category you care about. Matching on the finer details (mode, budget, year) is the map's
    // opt-in narrowing, and turning it on here would leave this panel emptier than the map.
    // limit keeps this panel's search well under the map's own, unbounded one - this only ever
    // shows MAX_SUGGESTIONS results, so there is no reason to pay for the network's full fan-out
    // of pages (which is what made this panel slow to load).
    const params = { radiusKm: RADIUS_KM, matchAttributes: false, limit: MAX_SUGGESTIONS }
    const search = isDemand
      ? discoveryApi.providers(params, controller.signal).then((res) => ({
          centre: res,
          items: res.providers.map(fromProvider),
        }))
      : discoveryApi.learners(params, controller.signal).then((res) => ({
          centre: res,
          items: res.learners.map(fromLearner),
        }))
    search
      .then(({ centre, items }) => {
        // Nearest first, whatever order the server used, and only the closest few.
        setSuggestions([...items].sort((a, b) => a.distanceKm - b.distanceKm).slice(0, MAX_SUGGESTIONS))
        // No saved coordinates comes back as a search centred on 0,0 with nobody in it.
        setHasLocation(!(centre.centreLat === 0 && centre.centreLng === 0))
        setStatus('ready')
      })
      .catch((err) => {
        if (controller.signal.aborted || (err as { name?: string })?.name === 'AbortError') return
        setStatus('failed')
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemand, roleKey, needsKey, needsState])

  return (
    <section className="card-pad">
      <h2 className="flex items-center gap-2 text-base font-bold text-ink-900">
        <Sparkles size={16} className="text-sun-500" /> Recommended for you
      </h2>
      <p className="mb-3 mt-0.5 text-xs text-ink-500">
        {isDemand
          ? `Tutors, centres, schools and more within ${RADIUS_KM} km, in the categories you are looking for.`
          : `Learners within ${RADIUS_KM} km who are looking for what you offer.`}
      </p>

      {status === 'loading' ? (
        <p className="flex items-center gap-1.5 text-sm text-ink-500">
          <LoaderCircle size={14} className="animate-spin" /> Finding people near you...
        </p>
      ) : status === 'failed' ? (
        <p className="text-sm text-ink-500">
          We could not load suggestions just now. You can still{' '}
          <Link to="/map" className="font-semibold text-amber-700 underline">
            open the map
          </Link>
          .
        </p>
      ) : isDemand && needs.length === 0 ? (
        <EmptyState
          title="Tell us what you are looking for"
          body={
            'Add what you need in "Looking for" and we will suggest matching tutors, centres, ' +
            `schools and more within ${RADIUS_KM} km of you.`
          }
          action={
            <button
              type="button"
              className="btn-sun mt-3 !px-3 !py-1.5 text-xs"
              onClick={() =>
                document.getElementById('needs')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            >
              Add what I am looking for
            </button>
          }
        />
      ) : !hasLocation ? (
        <EmptyState
          title="Add your location to see who is nearby"
          body={`Suggestions come from people within ${RADIUS_KM} km. Add your pincode and address so we know where to look.`}
        />
      ) : suggestions.length === 0 ? (
        <EmptyState
          title={`Nobody within ${RADIUS_KM} km yet`}
          body={
            isDemand
              ? 'New providers join all the time. The map lets you search a larger area.'
              : 'New learners join all the time. Keep your services up to date, or search a larger area on the map.'
          }
          action={
            <Link to="/map" className="btn-sun mt-3 !px-3 !py-1.5 text-xs">
              Open the YellowDots Map
            </Link>
          }
        />
      ) : (
        <>
          <ul className="space-y-2.5">
            {suggestions.map((s) => (
              <SuggestionRow key={s.id} suggestion={s} />
            ))}
          </ul>
          <Link to="/map" className="mt-3 inline-block text-xs font-semibold text-amber-700 underline">
            See everyone near you on the map
          </Link>
        </>
      )}
    </section>
  )
}

function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="py-3 text-center">
      <p className="text-sm font-semibold text-ink-900">{title}</p>
      <p className="mx-auto mt-1 text-xs text-ink-500">{body}</p>
      {action}
    </div>
  )
}

function SuggestionRow({ suggestion: s }: { suggestion: Suggestion }) {
  const navigate = useNavigate()
  return (
    <li className="rounded-xl border border-ink-200 bg-white p-3">
      <div className="flex items-start gap-2.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-sun-400/20 text-sm font-bold text-amber-700">
          {s.title.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="break-words text-sm font-semibold leading-snug text-ink-900">{s.title}</div>
          <div className="text-xs text-ink-500">{s.subtitle}</div>
        </div>
      </div>

      {s.lines.length > 0 && (
        <div className="mt-2 space-y-0.5 text-xs text-ink-700">
          {s.lines.slice(0, 2).map((line) => (
            <div key={line} className="break-words">
              {line}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-500">
        <span className="inline-flex items-center gap-1">
          <MapPin size={12} /> {s.distanceKm} km
        </span>
        {s.rating && (
          <span className="inline-flex items-center gap-1">
            <Star size={12} className="text-sun-500" /> {s.rating.average.toFixed(1)} ({s.rating.count})
          </span>
        )}
        {s.verified && <VerifiedBadge status="VERIFIED" />}
      </div>

      {/* Nothing is sent from here: the map is where consent and the safety checks live. */}
      <button
        type="button"
        onClick={() => navigate('/map', { state: { focusId: s.id } })}
        className="btn-sun mt-2.5 w-full !px-3 !py-1.5 text-xs"
      >
        Connect
      </button>
    </li>
  )
}
